import { isLocal } from '@magicschool/utils/nextjs/metadata';
import { useCallback, useEffect, useState } from 'react';
import type { CookieYesBannerEvent, CookieYesConsentCategory, CookieYesConsentEvent } from '@/declarations/cookieYes';
import { getCurrentCookieYesConsent } from './getCurrentCookieYesConsent';

const SSR_SAFE_PENDING_CONSENT: Record<CookieYesConsentCategory, boolean> = {
  necessary: false,
  functional: false,
  analytics: false,
  performance: false,
  advertisement: false,
  other: false,
};

/**
 * React hook to handle changes in cookie consent for EU customers, specifically
 * to load and unload 3rd party integrations like Amplitude.
 *
 * This is not necessary for integrations loaded with a <script> tag, which if configured
 * correctly in CookieYes will be handled automatically.
 */
export const useCookieYesConsent = (): Record<CookieYesConsentCategory, boolean> => {
  // getCurrentCookieYesConsent's isLocal() branch never touches `window`, so calling it here is
  // still SSR-safe — outside isLocal(), fall back to the placeholder so the first client render
  // matches the server exactly, or React flags a hydration mismatch.
  const [currentConsent, setCurrentConsent] = useState<Record<CookieYesConsentCategory, boolean>>(
    isLocal() ? getCurrentCookieYesConsent() : SSR_SAFE_PENDING_CONSENT,
  );

  useEffect(() => {
    if (!isLocal()) {
      setCurrentConsent(getCurrentCookieYesConsent());
    }
  }, [setCurrentConsent]);

  const handleBannerLoad = useCallback(
    (event: Event) => {
      const { categories } = (event as CookieYesBannerEvent).detail;
      setCurrentConsent(categories);
    },
    [setCurrentConsent],
  );

  const handleConsentUpdate = useCallback(
    (event: Event) => {
      const { accepted, rejected } = (event as CookieYesConsentEvent).detail;

      const newConsentModel = {} as typeof currentConsent;

      accepted.forEach((key) => {
        newConsentModel[key] = true;
      });
      rejected.forEach((key) => {
        newConsentModel[key] = false;
      });

      setCurrentConsent(newConsentModel);
    },
    [setCurrentConsent],
  );

  useEffect(() => {
    document.addEventListener('cookieyes_banner_load', handleBannerLoad);
    document.addEventListener('cookieyes_consent_update', handleConsentUpdate);

    return () => {
      document.removeEventListener('cookieyes_banner_load', handleBannerLoad);
      document.removeEventListener('cookieyes_consent_update', handleConsentUpdate);
    };
  }, [handleBannerLoad, handleConsentUpdate]);

  return currentConsent;
};
